/**
 * API functions for event applications
 */
import { supabase } from '../supabase';
import { formatLocalDate } from '../dates';
import { normalizeSupabaseError } from './errors';
import type { EventApplication, ApplicationWithDetails } from '../../types/database';

export async function getMyApplications(userId: string): Promise<ApplicationWithDetails[]> {
  // First get the attendee profile (maybeSingle: no profile yet is ok, avoid 406)
  const { data: profile } = await supabase
    .from('attendee_profiles')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (!profile) return [];

  const { data, error } = await supabase
    .from('event_applications')
    .select(`
      *,
      event:events(*)
    `)
    .eq('attendee_profile_id', profile.id)
    .order('created_at', { ascending: false });

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

export async function getApplicationsByEvent(eventId: string): Promise<ApplicationWithDetails[]> {
  const { data, error } = await supabase
    .from('event_applications')
    .select(`
      *,
      attendee_profile:attendee_profiles(
        *,
        emergency_contact:emergency_contacts(*),
        church_info:church_info(*),
        personal_references:personal_references(*),
        faith_info:faith_info(*)
      ),
      reviewed_by:admin_users(*)
    `)
    .eq('event_id', eventId)
    .order('submitted_at', { ascending: false });

  if (error) throw normalizeSupabaseError(error);
  return data || [];
}

/**
 * Admin only: applications for an event with full profile and related data.
 * Uses applications Edge Function; returns 401 if not admin.
 */
export async function getApplicationsByEventDecrypted(eventId: string): Promise<ApplicationWithDetails[]> {
  const { data, error } = await supabase.functions.invoke('applications', {
    body: { event_id: eventId },
  });
  if (error) throw normalizeSupabaseError(error);
  if (data?.error) throw new Error(data.error);
  const raw = data?.data;
  if (raw == null) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return list as ApplicationWithDetails[];
}

/**
 * Admin only: single application by id with full profile and related data.
 * Uses applications Edge Function; returns 401 if not admin.
 */
export async function getApplicationByIdDecrypted(id: string): Promise<ApplicationWithDetails | null> {
  const { data, error } = await supabase.functions.invoke('applications', {
    body: { application_id: id },
  });
  if (error) throw normalizeSupabaseError(error);
  if (data?.error) throw new Error(data.error);
  const raw = data?.data;
  if (raw == null) return null;
  return (Array.isArray(raw) ? raw[0] : raw) as ApplicationWithDetails;
}

export async function createOrUpdateApplication(
  eventId: string,
  attendeeProfileId: string,
  customFieldResponses: Record<string, any>,
  status: 'draft' | 'submitted' = 'draft',
  selectedSignupOptions?: string[] | null
): Promise<EventApplication> {
  // Check if application already exists
  const { data: existing } = await supabase
    .from('event_applications')
    .select('id')
    .eq('event_id', eventId)
    .eq('attendee_profile_id', attendeeProfileId)
    .maybeSingle();

  const applicationData: Record<string, any> = {
    custom_field_responses: customFieldResponses,
    status,
    submitted_at: status === 'submitted' ? new Date().toISOString() : null,
  };

  // Only include selected_signup_options if provided
  if (selectedSignupOptions !== undefined) {
    applicationData.selected_signup_options = selectedSignupOptions;
  }

  if (existing) {
    const { data, error } = await supabase
      .from('event_applications')
      .update(applicationData)
      .eq('id', existing.id)
      .select()
      .maybeSingle();

    if (error) throw normalizeSupabaseError(error);
    if (!data) throw new Error('Not found');
    return data;
  } else {
    const { data, error } = await supabase
      .from('event_applications')
      .insert({
        event_id: eventId,
        attendee_profile_id: attendeeProfileId,
        ...applicationData,
      })
      .select()
      .single();

    if (error) throw normalizeSupabaseError(error);
    return data;
  }
}

export async function updateApplicationStatus(
  applicationId: string,
  status: string,
  adminNotes?: string,
  reviewedByAdminId?: string
): Promise<EventApplication> {
  const updates: any = {
    status,
    reviewed_at: new Date().toISOString(),
  };

  if (adminNotes !== undefined) {
    updates.admin_notes = adminNotes;
  }

  if (reviewedByAdminId) {
    updates.reviewed_by_admin_id = reviewedByAdminId;
  }

  const { data, error } = await supabase
    .from('event_applications')
    .update(updates)
    .eq('id', applicationId)
    .select()
    .maybeSingle();

  if (error) throw normalizeSupabaseError(error);
  if (!data) throw new Error('Not found');
  return data;
}

export async function deleteApplication(id: string): Promise<void> {
  const { error } = await supabase
    .from('event_applications')
    .delete()
    .eq('id', id);

  if (error) throw normalizeSupabaseError(error);
}

export function exportApplicationsToCSV(applications: ApplicationWithDetails[]): string {
  if (applications.length === 0) return '';

  // Get all possible custom field keys
  const customFieldKeys = new Set<string>();
  applications.forEach((app) => {
    Object.keys(app.custom_field_responses || {}).forEach((key) => customFieldKeys.add(key));
  });

  // Enhanced CSV headers for mission applications
  const headers = [
    // Application Info
    'Application ID',
    'Status',
    'Submitted Date',
    'Reviewed Date',
    'Reviewed By',
    'Admin Notes',
    
    // Personal Information
    'First Name',
    'Last Name',
    'Gender',
    'Date of Birth',
    'T-Shirt Size',
    'Email',
    'Cell Phone',
    'Home Phone',
    'Work Phone',
    
    // Address
    'Address Line 1',
    'Address Line 2',
    'City',
    'State',
    'Zip Code',
    'Country',
    
    // Emergency Contact
    'Emergency Contact First Name',
    'Emergency Contact Last Name',
    'Emergency Contact Phone',
    'Emergency Contact Address Line 1',
    'Emergency Contact Address Line 2',
    'Emergency Contact City',
    'Emergency Contact State',
    'Emergency Contact Zip',
    'Emergency Contact Country',
    
    // Church Information
    'Church Name',
    'Pastor First Name',
    'Pastor Last Name',
    'Church Phone',
    'Church Address Line 1',
    'Church Address Line 2',
    'Church City',
    'Church State',
    'Church Zip',
    'Church Country',
    
    // Personal References
    'Reference 1 First Name',
    'Reference 1 Last Name',
    'Reference 1 Phone',
    'Reference 1 Address Line 1',
    'Reference 1 Address Line 2',
    'Reference 1 City',
    'Reference 1 State',
    'Reference 1 Zip',
    'Reference 1 Country',
    'Reference 2 First Name',
    'Reference 2 Last Name',
    'Reference 2 Phone',
    'Reference 2 Address Line 1',
    'Reference 2 Address Line 2',
    'Reference 2 City',
    'Reference 2 State',
    'Reference 2 Zip',
    'Reference 2 Country',
    
    // Faith Information
    'Born Again Believer',
    'Length of Faith',
    'Personal Testimony',
    'Evangelism Experience',
    'Currently Practicing Evangelism',
    'Previous Mission Trips',
    'Mission Trip Experience',
    
    // Custom Field Responses
    ...Array.from(customFieldKeys),
  ];

  // CSV rows
  const rows = applications.map((app) => {
    const profile = app.attendee_profile;
    const emergency = profile?.emergency_contact;
    const church = profile?.church_info;
    const references = profile?.personal_references || [];
    const faith = profile?.faith_info;
    
    // Find references by number
    const ref1 = references.find(r => r.reference_number === 1);
    const ref2 = references.find(r => r.reference_number === 2);
    
    return [
      // Application Info
      app.id,
      app.status,
      app.submitted_at ? new Date(app.submitted_at).toLocaleString() : 'Not submitted',
      app.reviewed_at ? new Date(app.reviewed_at).toLocaleString() : '',
      app.reviewed_by?.full_name || '',
      app.admin_notes || '',
      
      // Personal Information
      profile?.first_name || '',
      profile?.last_name || '',
      profile?.gender || '',
      profile?.date_of_birth ? formatLocalDate(profile.date_of_birth) : '',
      profile?.tshirt_size || '',
      profile?.email || '',
      profile?.cell_phone || '',
      profile?.home_phone || '',
      profile?.work_phone || '',
      
      // Address
      profile?.address_line1 || '',
      profile?.address_line2 || '',
      profile?.city || '',
      profile?.state || '',
      profile?.zip || '',
      profile?.country || '',
      
      // Emergency Contact
      emergency?.first_name || '',
      emergency?.last_name || '',
      emergency?.phone_number || '',
      emergency?.address_line1 || '',
      emergency?.address_line2 || '',
      emergency?.city || '',
      emergency?.state || '',
      emergency?.zip || '',
      emergency?.country || '',
      
      // Church Information
      church?.church_name || '',
      church?.pastor_first_name || '',
      church?.pastor_last_name || '',
      church?.phone_number || '',
      church?.address_line1 || '',
      church?.address_line2 || '',
      church?.city || '',
      church?.state || '',
      church?.zip || '',
      church?.country || '',
      
      // Personal References
      ref1?.first_name || '',
      ref1?.last_name || '',
      ref1?.phone_number || '',
      ref1?.address_line1 || '',
      ref1?.address_line2 || '',
      ref1?.city || '',
      ref1?.state || '',
      ref1?.zip || '',
      ref1?.country || '',
      ref2?.first_name || '',
      ref2?.last_name || '',
      ref2?.phone_number || '',
      ref2?.address_line1 || '',
      ref2?.address_line2 || '',
      ref2?.city || '',
      ref2?.state || '',
      ref2?.zip || '',
      ref2?.country || '',
      
      // Faith Information
      faith?.is_born_again_believer ? 'Yes' : 'No',
      faith?.believer_length || '',
      faith?.personal_testimony || '',
      faith?.evangelism_experience || '',
      faith?.currently_practicing_evangelism ? 'Yes' : 'No',
      faith?.has_been_on_mission_trip ? 'Yes' : 'No',
      faith?.mission_trip_experience || '',
      
      // Custom Field Responses
      ...Array.from(customFieldKeys).map((key) => {
        const value = app.custom_field_responses?.[key];
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return JSON.stringify(value);
        return value;
      }),
    ];
  });

  // Convert to CSV string
  const csvContent = [
    headers.map((h) => `"${h}"`).join(','),
    ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
  ].join('\n');

  return csvContent;
}
